import { addMonths, endOfWeek, isAfter, isBefore } from 'date-fns';
import { sortBy } from 'lodash';
import { ProjectedOptionCountRow } from 'src/domain/table/projected-option-count.row';
import { IntersectedStyleTimelineData } from 'src/hooks/style-timeline/queries/useStyleTimelineLevelQuery';

import {
    ChainFurnitureModel,
    ChainFurnitureZoneModel,
    PlanningRangeEnum,
    StyleCategoryModel,
} from 'src/infrastructure/rest-api/api-types';
import { formatWeekYearAsDate } from 'src/utils/formatAsWeekYear';
import { monthKeyToDateRange, monthKeyToField } from 'src/utils/monthKeys';

export enum HeatMapTypeEnum {
    Category,
    Furniture,
    Zone,
}

export interface Range<T = Date> {
    start: Date;
    end: T;
}

export function getOverlapRangeWithinMonth(range: Range<Date | null>, monthRange: Range): Range<Date | null> | null {
    let startDate: Date | null = null;
    let endDate: Date | null = null;

    if (range.start > monthRange.start) {
        startDate = range.start;
    } else {
        startDate = monthRange.start;
    }

    if (!range.end && monthRange.end) {
        endDate = monthRange.end;
    }

    if (range.end && !monthRange.end) {
        endDate = range.end;
    }

    if (range.end && range.end < monthRange.end) {
        endDate = range.end;
    }

    if (range.end && range.end > monthRange.end) {
        endDate = monthRange.end;
    }

    if (startDate > monthRange.end) {
        // invalid - not in month
        return null;
    }

    if (endDate && endDate > monthRange.end) {
        endDate = monthRange.end;
    }

    return {
        start: startDate,
        end: endDate,
    };
}

export function isStyleTimelineModelInMonth(model: IntersectedStyleTimelineData, monthKey: number) {
    // 1. convert month to date range
    const { monthStartDate, monthEndDate } = monthKeyToDateRange(monthKey);
    const startDate = new Date(model.startDate);
    const exitWeekDate = model?.exitWeek ? formatWeekYearAsDate(model.exitWeek) : null;

    // If start date is after month end or exit week is before month start, then the model is not included in the month
    if (startDate > monthEndDate || (exitWeekDate && exitWeekDate < monthStartDate)) {
        return false;
    }

    // 2. get start and end dates for overlap within month
    const overlapRange = getOverlapRangeWithinMonth(
        {
            start: startDate,
            end: exitWeekDate,
        },
        {
            start: monthStartDate,
            end: monthEndDate,
        }
    );

    if (!overlapRange) {
        return false;
    }

    // 3. if packaway has been set, check if packaway overlaps with the start/end dates
    const packawayStartWeekDate = model.packawayStartWeek ? formatWeekYearAsDate(model.packawayStartWeek) : null;
    const packawayEndWeekDate = model.packawayEndWeek ? endOfWeek(formatWeekYearAsDate(model.packawayEndWeek)) : null;

    const isPackedAway =
        packawayStartWeekDate && packawayEndWeekDate
            ? !(
                  isBefore(overlapRange.start, packawayStartWeekDate) ||
                  overlapRange.end === null ||
                  isAfter(overlapRange.end, packawayEndWeekDate)
              )
            : false;

    return !isPackedAway;
}

export function groupByFurniture(data: IntersectedStyleTimelineData[], furnitures: ChainFurnitureModel[]) {
    const initialMap = new Map<number, IntersectedStyleTimelineData[]>(
        furnitures.map((furniture) => [furniture.id, []])
    );

    return data.reduce((acc, model) => {
        const furnitureId = model?.chainFurnitureId;
        if (!furnitureId) {
            return acc;
        }

        const items = acc.get(furnitureId) ?? [];
        items.push(model);

        acc.set(furnitureId, items);

        return acc;
    }, initialMap);
}

export function groupByZone(data: IntersectedStyleTimelineData[], zones: ChainFurnitureZoneModel[]) {
    const initialMap = new Map<number, IntersectedStyleTimelineData[]>(zones.map((zone) => [zone.id, []]));

    return data.reduce((acc, model) => {
        const zoneId = model?.chainFurnitureZoneId;
        if (!zoneId) {
            return acc;
        }

        const items = acc.get(zoneId) ?? [];
        items.push(model);

        acc.set(zoneId, items);

        return acc;
    }, initialMap);
}

export interface MapToProjectedOptionCountHeatMapRowsProps {
    styleTimelineData: IntersectedStyleTimelineData[];
    styleCategories: StyleCategoryModel[];
    type: HeatMapTypeEnum;
    planningRange?: PlanningRangeEnum;
    furnitures: ChainFurnitureModel[];
    zones: ChainFurnitureZoneModel[];
}

export interface MapToProjectedOptionCountHeatMapRowsReturnType {
    rows: ProjectedOptionCountRow[];
}

function getHeatMapRow(models: IntersectedStyleTimelineData[], monthKeys: number[]) {
    return monthKeys.reduce(
        (row, monthKey) => {
            const monthField = monthKeyToField(monthKey);

            if (typeof row.columns[monthField] !== 'number') {
                row.columns[monthField] = 0;
            }

            models.forEach((model) => {
                if (isStyleTimelineModelInMonth(model, monthKey)) {
                    row.columns[monthField] += 1;
                }
            });

            if (row.columns[monthField] > row.maxValue) {
                row.maxValue = row.columns[monthField];
            }

            if (row.columns[monthField] < row.minValue) {
                row.minValue = row.columns[monthField];
            }

            return row;
        },
        {
            minValue: Number.MAX_SAFE_INTEGER,
            maxValue: 0,
            columns: {},
            furnitureTypeId: undefined,
        } as ProjectedOptionCountRow
    );
}

export function createSumRow(rows: ProjectedOptionCountRow[]): ProjectedOptionCountRow {
    const sumRow = rows.reduce<ProjectedOptionCountRow>(
        (acc, row) => {
            Object.keys(row.columns).forEach((key) => {
                if (!acc.columns[key]) {
                    acc.columns[key] = 0;
                }
                if (typeof row.columns[key] === 'number' && !['Left', 'Right'].includes(row.category)) {
                    acc.columns[key] += row.columns[key];
                }
            });

            return acc;
        },
        {
            id: 9999,
            category: 'Total',
            minValue: Number.MAX_SAFE_INTEGER,
            maxValue: 0,
            columns: {},
            furnitureTypeId: undefined,
        } as ProjectedOptionCountRow
    );

    Object.keys(sumRow.columns).forEach((key) => {
        if (sumRow.columns[key] > sumRow.maxValue) {
            sumRow.maxValue = sumRow.columns[key];
        }

        if (sumRow.columns[key] < sumRow.minValue) {
            sumRow.minValue = sumRow.columns[key];
        }
    });

    return sumRow;
}

export function createCategoryRows(
    monthKeys: number[],
    styleTimelineData: IntersectedStyleTimelineData[],
    styleCategories: StyleCategoryModel[] | undefined
): ProjectedOptionCountRow[] {
    // For each category, get the models and then for each month, check if the model is in the month

    const sortedStyleCategories = sortBy(styleCategories, (x) => x?.order);
    const rows = sortedStyleCategories.reduce<ProjectedOptionCountRow[]>((acc, styleCategory) => {
        const models = styleTimelineData.filter((model) => model?.styleCategoryId === styleCategory?.id);
        const row = getHeatMapRow(models, monthKeys);

        const categoryId = styleCategory?.id;
        if (!categoryId) return acc;

        row.id = styleCategory?.id;
        row.category = styleCategory?.name ?? '';
        acc.push(row);

        return acc;
    }, []);

    return rows;
}

export function createFurnitureRows(
    monthKeys: number[],
    styleTimelineData: IntersectedStyleTimelineData[],
    furnitures: ChainFurnitureModel[]
) {
    const groupedByFurniture = groupByFurniture(styleTimelineData, furnitures);

    const rows = Array.from(groupedByFurniture.entries()).map(([furnitureId, models]) => {
        const row = getHeatMapRow(models, monthKeys);
        const furniture = furnitures.find((f) => f.id === furnitureId);
        row.id = furnitureId;
        row.category = furniture?.name ?? 'Unknown';
        row.furnitureTypeId = furniture?.furnitureTypeId;
        return row;
    });

    return rows;
}

export function createZoneRows(
    monthKeys: number[],
    styleTimelineData: IntersectedStyleTimelineData[],
    zones: ChainFurnitureZoneModel[]
) {
    const groupedByZone = groupByZone(styleTimelineData, zones);

    const rows = Array.from(groupedByZone.entries()).map(([zoneId, models]) => {
        const row = getHeatMapRow(models, monthKeys);
        const zone = zones.find((z) => z.id === zoneId);
        row.id = zoneId;
        row.category = zone?.name ?? 'Unknown';
        row.furnitureTypeId = undefined;
        return row;
    });

    return rows;
}

export function mapStyleTimelineDataToProjectedOptionCountHeatMapRows({
    styleTimelineData,
    styleCategories,
    type,
    planningRange,
    furnitures,
    zones,
}: MapToProjectedOptionCountHeatMapRowsProps): MapToProjectedOptionCountHeatMapRowsReturnType {
    // Define month keys to include in the table
    const monthKeysLength = planningRange === 'EIGHTEEN_MONTHS' ? 18 : 12;
    const monthKeys = Array.from({ length: monthKeysLength }).map((_, index) => {
        const date = new Date();
        const newDate = addMonths(date, index);

        return Number(`${newDate.getFullYear()}${(newDate.getMonth() + 1).toString().padStart(2, '0')}`);
    });

    let rows: ProjectedOptionCountRow[] = [];

    if (type === HeatMapTypeEnum.Category) {
        rows = createCategoryRows(monthKeys, styleTimelineData, styleCategories);
    } else if (type === HeatMapTypeEnum.Furniture) {
        rows = createFurnitureRows(monthKeys, styleTimelineData, furnitures);
    } else {
        rows = createZoneRows(monthKeys, styleTimelineData, zones);
    }

    const monthSumValues = createSumRow(rows);
    rows.push(monthSumValues);

    return { rows };
}
